import { fetchDataCapture, postSubmission } from './api'
import { renderFormFromDataCapture, renderLoadError } from './dom'
import type { RenderOptions, SubmissionPayload } from './types'
import { formatDateIso } from './utils'
import { ensureFirebase } from './firebase'

const DEFAULT_MOUNT_ELEMENT_ID = 'return-lottery'
let scriptElement: HTMLScriptElement | null = null

function disableForm(form: HTMLFormElement, disabled: boolean) {
  form.querySelectorAll<HTMLElement>('input, select, textarea, button').forEach((el) => {
    ;(el as HTMLInputElement | HTMLButtonElement).disabled = disabled
  })
}

function showSpinner(form: HTMLFormElement, on: boolean) {
  const btn = form.querySelector('button[type=submit]') as HTMLButtonElement | null
  if (!btn) return
  let spinner = btn.querySelector('.rlv-spinner') as HTMLElement | null
  if (!spinner && on) {
    spinner = document.createElement('span')
    spinner.className = 'rlv-spinner'
    btn.prepend(spinner)
  }
  if (spinner) spinner.style.display = on ? 'inline-block' : 'none'
}

async function initAt(mount: HTMLElement, opts: RenderOptions) {
  try {
    ensureFirebase()
    const dc = await fetchDataCapture(opts)
    if (!dc || Object.keys(dc).length === 0) throw new Error('Empty data_capture')

    renderFormFromDataCapture(mount, dc)

    const form = mount.querySelector('form') as HTMLFormElement | null
    if (!form) throw new Error('Form not rendered')

    // Hook submit → POST to Firebase Function with App Check
    form.addEventListener(
      'submit',
      async (e) => {
        e.preventDefault()
        const err = form.querySelector('.rlv-errorbox') as HTMLElement | null
        if (err) err.style.display = 'none'

        // Gather form data
        const data: Record<string, unknown> = {}
        new FormData(form).forEach((val, key) => {
          data[key] = val
        })

        const payload: SubmissionPayload = {
          eventId: opts.eventId,
          createdAt: formatDateIso(),
          data,
          metadata: {
            userAgent: navigator.userAgent,
            lang: navigator.language
          }
        }

        disableForm(form, true)
        showSpinner(form, true)

        try {
          const submitUrl =
            opts.submitUrl ||
            (document.currentScript as HTMLScriptElement | null)?.dataset.submitUrl ||
            'https://rlv.sh/testing'
          if (!submitUrl) throw new Error('Missing submitUrl')
          const res = await postSubmission(submitUrl, payload)
          console.log('----')
          console.log(JSON.stringify(res))
          console.log('----')
          if (res.data.success === true) {
            mount.innerHTML = `<div class="rlv-success">You're in! Check your inbox to confirm your entry.</div>`
          } else {
            const msg = `Submission failed: ${res.data.message}`
            if (err) {
              err.textContent = msg
              err.style.display = 'block'
            }
            disableForm(form, false)
            showSpinner(form, false)
          }
        } catch (submitErr: any) {
          console.error(submitErr)
          if (err) {
            err.textContent = submitErr?.message || 'There was an error. Please try again.'
            err.style.display = 'block'
          }
          disableForm(form, false)
          showSpinner(form, false)
        }
      },
      { once: false }
    )
  } catch (e) {
    console.error(e)
    // Load failure → only error box, no form
    renderLoadError(mount, "We couldn't load the form right now.")
  }
}

export async function init(opts?: Partial<RenderOptions>) {
  const eventId = opts?.eventId || scriptElement?.dataset.eventId || ''

  if (!eventId) {
    console.warn('ReturnLottery: missing event id (data-event-id)')
    return
  }
  const mount = document.getElementById(DEFAULT_MOUNT_ELEMENT_ID)
  if (!mount) {
    console.warn(`ReturnLottery: mount #${DEFAULT_MOUNT_ELEMENT_ID} not found`)
    return
  }
  mount.dataset.eventId = eventId
  await initAt(mount, { eventId })
}

// Entry point. Make sure the script is loaded at all times
if (typeof window !== 'undefined') {
  scriptElement = document.currentScript as HTMLScriptElement
  document.addEventListener('DOMContentLoaded', () => {
    void init()
  })
} else {
  void init()
}
